import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import ListGroup from 'react-bootstrap/ListGroup'
import Row from 'react-bootstrap/Row'
import Spinner from 'react-bootstrap/Spinner'
import ToggleButton from 'react-bootstrap/ToggleButton'
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
    cancelReceiptMcpHandoff,
    confirmReceipt,
    deleteReceipt,
    updateReceipt,
    type Currency,
    type GroupDetail,
    type GroupMember,
    type Receipt,
    type ReceiptCurrencyConversion,
    type Transaction,
    type UpdateReceiptRequest,
} from '../../../../types'
import { actionApiCall, formSubmit } from '../../api/actionApiCall'
import { AmountField, SelectField, SubmitButton, TextField, useFormState, type FieldErrors } from '../../components/form'
import { useCapabilities } from '../../hooks/useCapabilities'
import { useCurrencies } from '../../hooks/useCurrencies'
import { useGroup } from '../../hooks/useGroups'
import {
    cekaNaAsistenta,
    lzePorovnat,
    receiptQueryKey,
    seRozpoznava,
    stavKontroly,
    useInvalidateReceiptQueue,
    useInvalidateReceipts,
    useReceipt,
    useReceiptAnalyses,
    useTransaction,
} from '../../hooks/useReceipts'
import { nazevRozpoznavani } from '../../hooks/useZpracovaniUctenek'
import { useOnlineStatus } from '../../offline/useOnlineStatus'
import { exponentOf, formatAmount, formatMoney, parseAmount, toAmountInputValue } from '../../utils/money'
import { ForeignCurrencyAlert, jeCiziMenaUctenky, poznamkaKPrepoctu } from '../groups/ForeignCurrencyAlert'
import { nepriradenePolozky, ReceiptItemsSplit } from '../groups/ReceiptItemsSplit'
import { StavUctenky } from '../groups/ReceiptReviewSection'
import { kdoPrecetl, ReceiptAnalysesModal } from './ReceiptAnalysesModal'
import { ReceiptRecheckMenu } from './ReceiptRecheckMenu'

/**
 * Kontrola nahrané účtenky - fotka vedle rozpoznaných údajů.
 *
 * Dokud účtenku čte server nebo se čeká na AI asistenta, stránka ukazuje jen
 * fotku a stav. Pak tu lidé ze skupiny opraví údaje, rozeberou si položky
 * a útratu potvrdí. Všechno se průběžně ukládá k účtence, takže kontrolu jde
 * přerušit a dokončit později - klidně někým jiným ze skupiny.
 *
 * Táž stránka slouží i k úpravě útraty, která z účtenky už vznikla: formulář
 * se vyplní z útraty a místo potvrzení se změny uloží do ní.
 */
export function ReceiptReviewPage() {
    const { groupId = '', receiptId = '' } = useParams<{ groupId: string; receiptId: string }>()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const online = useOnlineStatus()
    const invalidateFrontu = useInvalidateReceiptQueue()

    const invalidateReceipts = useInvalidateReceipts()

    const { data: group, isLoading: groupLoading } = useGroup(groupId)
    const { data: receipt, isLoading: receiptLoading } = useReceipt(receiptId)
    const { data: currencies } = useCurrencies()
    // Potvrzená účtenka - upravuje se útrata, která z ní vznikla.
    const transactionId = receipt?.transactionId ?? ''
    const { data: transaction, isLoading: transactionLoading } = useTransaction(transactionId)
    const detailUtraty = `/skupiny/${groupId}/pohyby/${transactionId}/uctenka`

    // Po použití jiného výsledku rozpoznání se formulář vyplní znovu - z něj.
    const [verzeVysledku, setVerzeVysledku] = useState(0)

    const aktualizuj = (aktualni: Receipt) => {
        queryClient.setQueryData(receiptQueryKey(aktualni.id), aktualni)
        // Sekce Ke kontrole ukazuje stav a rozebrané položky - ať sedí i po návratu.
        void invalidateFrontu(groupId)
    }

    const smazat = async () => {
        if (!window.confirm('Opravdu účtenku smazat i s fotkou? Tuto akci nelze vrátit.')) {
            return
        }
        const vysledek = await actionApiCall(() => deleteReceipt({ path: { receiptId } }), { successMessage: 'Účtenka byla smazána.' })
        if (vysledek !== undefined) {
            if (transactionId) {
                await invalidateReceipts(transactionId)
                navigate(detailUtraty)
                return
            }
            await invalidateFrontu(groupId)
            navigate(`/skupiny/${groupId}`)
        }
    }

    const zpet = transactionId ? (
        <Link to={detailUtraty} className="small">
            <i className="bi bi-arrow-left me-1" aria-hidden="true" />
            Zpět na útratu
        </Link>
    ) : (
        <Link to={`/skupiny/${groupId}`} className="small">
            <i className="bi bi-arrow-left me-1" aria-hidden="true" />
            Zpět na skupinu
        </Link>
    )

    if (groupLoading || receiptLoading || transactionLoading || !currencies) {
        return <Spinner animation="border" />
    }

    if (!receipt || !group) {
        return (
            <>
                <div className="mb-3">{zpet}</div>
                <Alert variant="warning">Účtenka nebyla nalezena. Možná ji mezitím někdo potvrdil nebo smazal.</Alert>
            </>
        )
    }

    if (receipt.transactionId && !transaction) {
        return (
            <>
                <div className="mb-3">{zpet}</div>
                <Alert variant="warning">Útrata, ke které účtenka patří, nebyla nalezena.</Alert>
            </>
        )
    }

    const zpracovava = stavKontroly(receipt) === 'ZPRACOVAVA'

    return (
        <div data-testid="kontrola-uctenky">
            <div className="mb-3">
                {zpet}
                <div className="d-flex align-items-center justify-content-between gap-2 mt-2 flex-wrap">
                    <h1 className="h4 mb-0">{transaction ? 'Úprava útraty' : 'Kontrola účtenky'}</h1>
                    {!transaction && <StavUctenky receipt={receipt} />}
                </div>
            </div>

            {!online && (
                <Alert variant="secondary" className="py-2 small">
                    <i className="bi bi-wifi-off me-1" aria-hidden="true" />
                    Offline — účtenku teď nelze upravovat.
                </Alert>
            )}

            <Row className="g-3">
                <Col lg={5}>
                    <FotkyUctenky receipt={receipt} />
                </Col>
                <Col lg={7}>
                    {zpracovava ? (
                        <ZpracovaniUctenky receipt={receipt} canWrite={online} onReceiptChange={aktualizuj} />
                    ) : (
                        <>
                            <RozpoznaniUctenky
                                receipt={receipt}
                                canWrite={online}
                                onReceiptChange={aktualizuj}
                                onApplied={(aktualni) => {
                                    aktualizuj(aktualni)
                                    setVerzeVysledku((verze) => verze + 1)
                                }}
                            />
                            <KontrolaUctenky
                                key={`${verzeVysledku}-${transaction?.version ?? ''}`}
                                group={group}
                                receipt={receipt}
                                transaction={transaction}
                                currencies={currencies}
                                canWrite={online}
                                onReceiptChange={aktualizuj}
                                onConfirmed={() => {
                                    // Účtenka teď patří útratě - stará kopie v cache by příště
                                    // otevřela znovu kontrolu místo úpravy.
                                    queryClient.removeQueries({ queryKey: receiptQueryKey(receipt.id) })
                                    if (transaction) {
                                        void invalidateReceipts(transaction.id)
                                        navigate(detailUtraty)
                                        return
                                    }
                                    void invalidateFrontu(groupId)
                                    navigate(`/skupiny/${groupId}`)
                                }}
                            />
                        </>
                    )}

                    <div className="d-flex justify-content-end mt-3">
                        <Button variant="outline-danger" size="sm" className="su-tappable" disabled={!online} onClick={() => void smazat()}>
                            <i className="bi bi-trash me-1" aria-hidden="true" />
                            Smazat účtenku
                        </Button>
                    </div>
                </Col>
            </Row>
        </div>
    )
}

/** Fotky účtenky - náhled, po klepnutí v plné velikosti; dlouhá účtenka po stranách. */
function FotkyUctenky({ receipt }: { receipt: Receipt }) {
    const [velka, setVelka] = useState(false)
    const [fotka, setFotka] = useState(1)
    const cislo = Math.min(fotka, receipt.pageCount)

    return (
        <Card className="sticky-lg-top" style={{ top: '1rem' }}>
            <button
                type="button"
                className="btn p-0 border-0"
                onClick={() => setVelka((soucasna) => !soucasna)}
                aria-label={velka ? 'Zmenšit účtenku' : 'Zvětšit účtenku'}
            >
                {/* Fotka se odkazuje přímo URL - přes fetch by se musela převádět na blob. */}
                <img
                    src={`/api/receipts/${receipt.id}/file?variant=${velka ? 'full' : 'thumb'}&page=${cislo}`}
                    alt={`Fotografie účtenky${receipt.pageCount > 1 ? `, ${cislo}. fotka` : ''}`}
                    className="img-fluid rounded-top"
                    style={{ maxHeight: velka ? 'none' : '24rem', objectFit: 'contain', width: '100%' }}
                />
            </button>
            <Card.Footer className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                <a href={`/api/receipts/${receipt.id}/file?page=${cislo}`} target="_blank" rel="noreferrer" className="small">
                    <i className="bi bi-box-arrow-up-right me-1" aria-hidden="true" />
                    Otevřít celou
                </a>
                {receipt.pageCount > 1 && (
                    <div className="btn-group btn-group-sm" role="group" aria-label="Fotky účtenky">
                        {Array.from({ length: receipt.pageCount }, (_, index) => index + 1).map((strana) => (
                            <button
                                key={strana}
                                type="button"
                                className={`btn su-tappable ${strana === cislo ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                aria-label={`Fotka ${strana} z ${receipt.pageCount}`}
                                onClick={() => setFotka(strana)}
                            >
                                {strana}
                            </button>
                        ))}
                    </div>
                )}
            </Card.Footer>
        </Card>
    )
}

type ZpracovaniUctenkyProps = {
    receipt: Receipt
    canWrite: boolean
    onReceiptChange: (receipt: Receipt) => void
}

/** Účtenku ještě čte server nebo se čeká na AI asistenta - co se právě děje. */
function ZpracovaniUctenky({ receipt, canWrite, onReceiptChange }: ZpracovaniUctenkyProps) {
    const { data: capabilities } = useCapabilities()
    const [rusim, setRusim] = useState(false)
    const nastroj = nazevRozpoznavani(capabilities?.receiptAnalysisProvider)
    const asistent = cekaNaAsistenta(receipt)
    // Server účtenku četl (nebo čte), pokud nečeká jen na asistenta.
    const server = receipt.analysisStatus !== 'AWAITING_MCP' && receipt.analysisStatus !== 'NONE'

    const necekat = async () => {
        setRusim(true)
        try {
            const aktualni = await actionApiCall(() => cancelReceiptMcpHandoff({ path: { receiptId: receipt.id } }), {
                successMessage: 'Na AI asistenta se už nečeká.',
            })
            if (aktualni) {
                onReceiptChange(aktualni)
            }
        } finally {
            setRusim(false)
        }
    }

    return (
        <Card body data-testid="zpracovani-uctenky">
            <h2 className="h6 d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                Účtenka se zpracovává
            </h2>

            <ListGroup variant="flush" className="small mb-3">
                {server && (
                    <ListGroup.Item className="px-0 d-flex gap-2">
                        {seRozpoznava(receipt) ? (
                            <>
                                <Spinner animation="grow" size="sm" className="mt-1 flex-shrink-0" />
                                <span>Čte ji {nastroj}…</span>
                            </>
                        ) : receipt.analysisStatus === 'FAILED' ? (
                            <>
                                <i className="bi bi-x-circle text-danger" aria-hidden="true" />
                                <span>{nastroj.charAt(0).toUpperCase() + nastroj.slice(1)} ji nepřečetl{receipt.analysisError ? `: ${receipt.analysisError}` : '.'}</span>
                            </>
                        ) : (
                            <>
                                <i className="bi bi-check-circle text-success" aria-hidden="true" />
                                <span>{nastroj.charAt(0).toUpperCase() + nastroj.slice(1)} — hotovo.</span>
                            </>
                        )}
                    </ListGroup.Item>
                )}
                {asistent && (
                    <ListGroup.Item className="px-0 d-flex gap-2">
                        <Spinner animation="grow" size="sm" className="mt-1 flex-shrink-0" />
                        <span>
                            Čeká na AI asistenta — požádejte ho o zpracování účtenek ve SplitUpu, výsledek se tu objeví sám.{' '}
                            <Link to="/ai-asistent">Jak asistenta připojit</Link>
                        </span>
                    </ListGroup.Item>
                )}
            </ListGroup>

            <p className="small text-secondary mb-3">
                Až bude hotovo, uvidíte tu rozpoznané položky a rozeberete je mezi lidi. Aplikaci můžete zatím zavřít.
            </p>

            {asistent && (
                <Button variant="outline-secondary" className="su-tappable" disabled={!canWrite || rusim} onClick={() => void necekat()}>
                    {rusim && <Spinner animation="border" size="sm" className="me-2" />}
                    Nečekat na asistenta
                </Button>
            )}
        </Card>
    )
}

type RozpoznaniUctenkyProps = {
    receipt: Receipt
    canWrite: boolean
    onReceiptChange: (receipt: Receipt) => void
    /** Uživatel použil jiný výsledek rozpoznání. */
    onApplied: (receipt: Receipt) => void
}

/** Kdo účtenku přečetl, výsledky k porovnání a „přečíst znovu". */
function RozpoznaniUctenky({ receipt, canWrite, onReceiptChange, onApplied }: RozpoznaniUctenkyProps) {
    const { data: capabilities } = useCapabilities()
    const { data: currencies } = useCurrencies()
    const vice = lzePorovnat(receipt)
    const { data: vysledky } = useReceiptAnalyses(receipt.id, vice)
    const [porovnani, setPorovnani] = useState(false)

    const kdo =
        receipt.analysisStatus === 'NONE'
            ? 'Účtenku nikdo nečetl — údaje doplňte ručně.'
            : receipt.analysisStatus === 'FAILED'
              ? `Rozpoznání se nezdařilo${receipt.analysisError ? `: ${receipt.analysisError}` : '.'} Údaje doplňte ručně.`
              : receipt.analysisProvider === 'mcp'
                ? 'Přečetl AI asistent. Výsledek je jen návrh — projděte ho.'
                : `Přečetl ${nazevRozpoznavani(receipt.analysisProvider ?? capabilities?.receiptAnalysisProvider)}. Výsledek je jen návrh — projděte ho.`

    return (
        <Card className="mb-3">
            <Card.Body className="pb-2">
                {!vice && <p className={`small mb-2 ${receipt.analysisStatus === 'FAILED' ? 'text-danger' : 'text-secondary'}`}>{kdo}</p>}

                {vice && (
                    <div className="mb-2" data-testid="vysledky-rozpoznani">
                        <div className="small text-secondary mb-1">
                            Účtenku přečetlo víc nástrojů. Do položek se promítá ten označený — porovnejte je a případně použijte jiný.
                        </div>
                        <ListGroup className="small">
                            {(vysledky ?? []).map((vysledek) => {
                                const mena = vysledek.currencyCode ?? receipt.detectedCurrencyCode ?? 'CZK'
                                return (
                                    <ListGroup.Item key={vysledek.id} className="d-flex justify-content-between align-items-center gap-2">
                                        <div className="min-w-0">
                                            <div className="fw-semibold">{kdoPrecetl(vysledek)}</div>
                                            <div className="text-secondary text-truncate">
                                                {vysledek.merchant ?? 'Bez názvu'} ·{' '}
                                                {vysledek.total !== null && vysledek.total !== undefined
                                                    ? formatMoney(vysledek.total, mena, exponentOf(currencies, mena))
                                                    : 'částka nepřečtena'}{' '}
                                                · {vysledek.items.length} {vysledek.items.length === 1 ? 'položka' : vysledek.items.length <= 4 && vysledek.items.length > 1 ? 'položky' : 'položek'}
                                            </div>
                                        </div>
                                        {vysledek.isApplied && <Badge bg="success">Použito</Badge>}
                                    </ListGroup.Item>
                                )
                            })}
                        </ListGroup>
                    </div>
                )}

                <div className="d-flex gap-2 flex-wrap">
                    {vice && (
                        <Button variant="outline-primary" size="sm" className="su-tappable" onClick={() => setPorovnani(true)}>
                            <i className="bi bi-layout-split me-1" aria-hidden="true" />
                            Porovnat a vybrat
                        </Button>
                    )}
                    <ReceiptRecheckMenu receipt={receipt} size="sm" disabled={!canWrite} onRequested={onReceiptChange} />
                </div>
            </Card.Body>

            {porovnani && (
                <ReceiptAnalysesModal show={porovnani} receipt={receipt} onClose={() => setPorovnani(false)} onApplied={onApplied} />
            )}
        </Card>
    )
}

type Hodnoty = {
    title: string
    amount: string
    currencyCode: string
    exchangeRate: string
    occurredAt: string
    payerId: string
    splitMode: 'ITEMS' | 'EQUAL'
    participantIds: string[]
    note: string
}

/** Hodnota pro <input type="datetime-local"> v místním čase. */
function toLocalInputValue(date: Date): string {
    const offsetMs = date.getTimezoneOffset() * 60_000
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16)
}

/** Kurz se zadává s desetinnou čárkou; prázdná hodnota znamená „podle ČNB". */
function parseRate(text: string): number | null {
    const normalized = text.replace(/\s| /g, '').replace(',', '.')
    if (normalized === '') {
        return null
    }
    const value = Number(normalized)
    return Number.isFinite(value) && value > 0 ? value : null
}

/** Údaje útraty, která z účtenky už vznikla - při její úpravě. */
function hodnotyZUtraty(transaction: Transaction, currencies: Currency[]): Hodnoty {
    return {
        title: transaction.title,
        amount: toAmountInputValue(transaction.amountTotal, exponentOf(currencies, transaction.currencyCode)),
        currencyCode: transaction.currencyCode,
        exchangeRate: transaction.exchangeRate ? String(transaction.exchangeRate).replace('.', ',') : '',
        occurredAt: toLocalInputValue(new Date(transaction.occurredAt)),
        payerId: transaction.payers[0]?.memberId ?? '',
        splitMode: transaction.splitMode === 'ITEMS' ? 'ITEMS' : 'EQUAL',
        participantIds: transaction.shares.map((share) => share.memberId),
        note: transaction.note ?? '',
    }
}

/** Výchozí údaje útraty - z účtenky, jak ji rozpoznávání (nebo někdo ze skupiny) vyplnil. */
function vychoziHodnoty(receipt: Receipt, group: GroupDetail, clenove: GroupMember[], currencies: Currency[]): Hodnoty {
    const mena =
        receipt.detectedCurrencyCode && group.currencyCodes.includes(receipt.detectedCurrencyCode) ? receipt.detectedCurrencyCode : group.baseCurrencyCode
    // Částku v měně, kterou skupina nevede, nejde použít - nabídne se přepočet.
    const castka = jeCiziMenaUctenky(receipt, group) ? undefined : (receipt.detectedTotal ?? (receipt.itemsTotal > 0 ? receipt.itemsTotal : undefined))

    return {
        title: receipt.detectedTitle ?? '',
        amount: toAmountInputValue(castka ?? undefined, exponentOf(currencies, mena)),
        currencyCode: mena,
        exchangeRate: '',
        occurredAt: toLocalInputValue(new Date(receipt.detectedAt ?? receipt.createdAt)),
        payerId: receipt.payerMemberId ?? clenove[0]?.id ?? '',
        // Účtenka s položkami se dělí podle nich - kvůli tomu ji lidé nahrávají.
        splitMode: receipt.items.length > 0 ? 'ITEMS' : 'EQUAL',
        participantIds: clenove.map((member) => member.id),
        note: '',
    }
}

/** Chyby polí ze serveru pod názvy polí formuláře. */
function chybyFormulare(chyby: FieldErrors | undefined): FieldErrors | undefined {
    if (!chyby) {
        return undefined
    }
    const { amountTotal, payerMemberId, items, splitMode, participantIds, ...zbytek } = chyby
    const prevedene: FieldErrors = { ...zbytek }
    if (amountTotal) prevedene.amount = amountTotal
    if (payerMemberId) prevedene.payerId = payerMemberId
    const deleni = items ?? splitMode ?? participantIds
    if (deleni) prevedene.split = deleni
    return prevedene
}

type KontrolaUctenkyProps = {
    group: GroupDetail
    receipt: Receipt
    /** Útrata, která z účtenky už vznikla - formulář ji pak upravuje. */
    transaction?: Transaction
    currencies: Currency[]
    canWrite: boolean
    onReceiptChange: (receipt: Receipt) => void
    onConfirmed: () => void
}

/**
 * Údaje útraty z účtenky a rozdělení mezi lidi.
 *
 * Název, částka, datum a plátce se průběžně ukládají k účtence (rozpracovaná
 * útrata), přiřazení položek ukládá rovnou `ReceiptItemsSplit`. Potvrzením
 * se z účtenky založí útrata.
 */
function KontrolaUctenky({ group, receipt, transaction, currencies, canWrite, onReceiptChange, onConfirmed }: KontrolaUctenkyProps) {
    // Neaktivní člen zůstává na výběr, pokud už v útratě je.
    const vUtrate = (memberId: string) =>
        !!transaction && (transaction.shares.some((share) => share.memberId === memberId) || transaction.payers.some((payer) => payer.memberId === memberId))
    const aktivni = useMemo(
        () => group.members.filter((member) => member.isActive || vUtrate(member.id)),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- útrata se během úpravy nemění
        [group.members],
    )
    const clenove = useMemo(
        () => group.members.filter((member) => aktivni.includes(member) || member.id === receipt.payerMemberId),
        [group.members, aktivni, receipt.payerMemberId],
    )
    const [pocatecni] = useState(() =>
        transaction ? hodnotyZUtraty(transaction, currencies) : vychoziHodnoty(receipt, group, aktivni, currencies),
    )

    const form = useFormState<Hodnoty>({
        initialValues: pocatecni,
        onSubmit: async (values) => {
            const amountTotal = parseAmount(values.amount, exponentOf(currencies, values.currencyCode))
            const podlePolozek = values.splitMode === 'ITEMS'

            if (!values.title.trim()) {
                return { fieldErrors: { title: 'Zadejte název útraty.' } }
            }
            if (amountTotal === undefined || amountTotal <= 0) {
                return { fieldErrors: { amount: 'Zadejte částku větší než nula.' } }
            }
            if (podlePolozek && receipt.items.length === 0) {
                return { fieldErrors: { split: 'Účtenka nemá položky — doplňte je, nebo útratu rozdělte rovným dílem.' } }
            }
            if (podlePolozek && nepriradenePolozky(receipt).length > 0) {
                return { fieldErrors: { split: 'Přiřaďte osoby ke všem položkám účtenky.' } }
            }
            if (!podlePolozek && values.participantIds.length === 0) {
                return { fieldErrors: { split: 'Vyberte alespoň jednu osobu.' } }
            }

            const { data, fieldErrors } = await formSubmit(
                () =>
                    confirmReceipt({
                        path: { receiptId: receipt.id },
                        body: {
                            splitMode: values.splitMode,
                            title: values.title.trim(),
                            note: values.note.trim() || null,
                            currencyCode: values.currencyCode,
                            amountTotal,
                            exchangeRate: values.currencyCode === group.baseCurrencyCode ? null : parseRate(values.exchangeRate),
                            occurredAt: new Date(values.occurredAt).toISOString(),
                            payerMemberId: values.payerId,
                            participantIds: podlePolozek ? undefined : values.participantIds,
                            version: transaction?.version,
                        },
                    }),
                { successMessage: transaction ? 'Útrata byla upravena.' : 'Útrata byla založena.' },
            )

            if (!data) {
                return { fieldErrors: chybyFormulare(fieldErrors) }
            }
            onConfirmed()
        },
    })

    const { values, setValue } = form

    // ─── Průběžné ukládání rozpracované útraty ──────────────────────────────
    // Chvíli po poslední změně se změněné údaje uloží k účtence - kontrolu tak
    // jde přerušit a dokončit později, nebo ji dokončí někdo jiný ze skupiny.
    const ulozene = useRef(pocatecni)
    const { title, amount, currencyCode, occurredAt, payerId } = values

    useEffect(() => {
        // U potvrzené účtenky se mění útrata, ne rozpracované údaje účtenky -
        // ta se ukládá až tlačítkem.
        if (!canWrite || transaction) {
            return
        }

        const casovac = window.setTimeout(() => {
            const minule = ulozene.current
            const zmeny: UpdateReceiptRequest = {}

            if (title !== minule.title) {
                zmeny.detectedTitle = title.trim() || null
            }
            if (occurredAt !== minule.occurredAt && occurredAt) {
                zmeny.detectedAt = new Date(occurredAt).toISOString()
            }
            if (payerId !== minule.payerId && payerId) {
                zmeny.payerMemberId = payerId
            }
            // Částka a měna patří k sobě - ukládají se jen spolu.
            if (amount !== minule.amount || currencyCode !== minule.currencyCode) {
                const castka = amount.trim() === '' ? null : parseAmount(amount, exponentOf(currencies, currencyCode))
                if (castka !== undefined) {
                    zmeny.detectedTotal = castka
                    zmeny.detectedCurrencyCode = currencyCode
                }
            }

            ulozene.current = { ...minule, title, occurredAt, payerId, amount, currencyCode }
            if (Object.keys(zmeny).length === 0) {
                return
            }
            void actionApiCall(() => updateReceipt({ path: { receiptId: receipt.id }, body: zmeny }), { silent: true }).then((aktualni) => {
                if (aktualni) {
                    onReceiptChange(aktualni)
                }
            })
        }, 800)

        return () => window.clearTimeout(casovac)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- ukládá se jen při změně údajů
    }, [title, amount, currencyCode, occurredAt, payerId, canWrite])

    // Přepočtená účtenka je v hlavní měně skupiny - převezme se částka
    // a do poznámky se zapíše, z čeho a jakým kurzem vznikla.
    const poPrepoctu = (prepocet: ReceiptCurrencyConversion) => {
        onReceiptChange(prepocet.receipt)
        const novaCastka = toAmountInputValue(prepocet.receipt.detectedTotal ?? undefined, exponentOf(currencies, prepocet.to))
        ulozene.current = { ...ulozene.current, currencyCode: prepocet.to, amount: novaCastka }
        setValue('currencyCode', prepocet.to)
        setValue('amount', novaCastka)
        const poznamka = poznamkaKPrepoctu(prepocet, exponentOf(currencies, prepocet.from))
        setValue('note', values.note ? `${values.note} ${poznamka}` : poznamka)
    }

    const exponent = exponentOf(currencies, values.currencyCode)
    const amountTotal = parseAmount(values.amount, exponent) ?? 0
    const symbol = currencies.find((currency) => currency.code === values.currencyCode)?.symbol
    const ciziMena = values.currencyCode !== group.baseCurrencyCode
    const podlePolozek = values.splitMode === 'ITEMS'

    const prepniUcastnika = (memberId: string, checked: boolean) => {
        setValue('participantIds', checked ? [...values.participantIds, memberId] : values.participantIds.filter((id) => id !== memberId))
        form.setFieldErrors(({ split: _odebrano, ...zbytek }) => zbytek)
    }

    return (
        <form onSubmit={form.handleSubmit} noValidate>
            {!transaction && jeCiziMenaUctenky(receipt, group) && <ForeignCurrencyAlert receipt={receipt} group={group} onConverted={poPrepoctu} />}

            {transaction && (
                <Alert variant="light" className="border py-2 small">
                    Upravujete útratu z této účtenky. Přiřazení položek se ukládá hned, do rozdělení útraty se ale promítne
                    až tlačítkem <strong>Uložit změny</strong>.
                </Alert>
            )}

            <Card className="mb-3">
                <Card.Body className="pb-0">
                    <TextField
                        label="Název"
                        value={values.title}
                        onChange={(hodnota) => setValue('title', hodnota)}
                        error={form.fieldErrors.title}
                        placeholder="Např. Albert"
                        disabled={!canWrite}
                        required
                    />

                    <Row>
                        <Col xs={12} sm={group.currencyCodes.length > 1 ? 7 : 12}>
                            <AmountField
                                label="Částka"
                                value={values.amount}
                                onChange={(hodnota) => setValue('amount', hodnota)}
                                currencySymbol={symbol}
                                error={form.fieldErrors.amount}
                                disabled={!canWrite}
                                required
                            />
                        </Col>
                        {group.currencyCodes.length > 1 && (
                            <Col xs={12} sm={5}>
                                <SelectField
                                    label="Měna"
                                    value={values.currencyCode}
                                    onChange={(hodnota) => setValue('currencyCode', hodnota)}
                                    error={form.fieldErrors.currencyCode}
                                    disabled={!canWrite}
                                    options={group.currencyCodes.map((code) => ({ value: code, label: code }))}
                                />
                            </Col>
                        )}
                    </Row>

                    {ciziMena && (
                        <AmountField
                            label={`Kurz (1 ${values.currencyCode} = ? ${group.baseCurrencyCode})`}
                            value={values.exchangeRate}
                            onChange={(hodnota) => setValue('exchangeRate', hodnota)}
                            error={form.fieldErrors.exchangeRate}
                            disabled={!canWrite}
                            helpText="Prázdné pole znamená kurz ČNB k datu útraty."
                        />
                    )}

                    <TextField
                        label="Datum a čas"
                        type="datetime-local"
                        value={values.occurredAt}
                        onChange={(hodnota) => setValue('occurredAt', hodnota)}
                        error={form.fieldErrors.occurredAt}
                        disabled={!canWrite}
                        required
                    />

                    <SelectField
                        label="Kdo zaplatil"
                        value={values.payerId}
                        onChange={(hodnota) => setValue('payerId', hodnota)}
                        error={form.fieldErrors.payerId}
                        disabled={!canWrite}
                        options={clenove.map((member) => ({ value: member.id, label: member.displayName }))}
                    />
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Body>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                        <Form.Label className="mb-0 fw-semibold">{podlePolozek ? 'Kdo co měl' : 'Rozdělit rovným dílem mezi'}</Form.Label>
                        <ToggleButtonGroup
                            type="radio"
                            name="zpusob-deleni"
                            size="sm"
                            value={values.splitMode}
                            onChange={(hodnota: Hodnoty['splitMode']) => {
                                setValue('splitMode', hodnota)
                                form.setFieldErrors(({ split: _odebrano, ...zbytek }) => zbytek)
                            }}
                        >
                            <ToggleButton id="deleni-polozky" value="ITEMS" variant="outline-secondary" disabled={!canWrite}>
                                Podle položek ({receipt.items.length})
                            </ToggleButton>
                            <ToggleButton id="deleni-rovnym" value="EQUAL" variant="outline-secondary" disabled={!canWrite}>
                                Rovným dílem
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </div>

                    {podlePolozek ? (
                        <ReceiptItemsSplit
                            receipt={receipt}
                            members={aktivni}
                            amountTotal={amountTotal}
                            currencyCode={values.currencyCode}
                            disabled={!canWrite || form.submitting}
                            onReceiptChange={onReceiptChange}
                        />
                    ) : (
                        <div className="border rounded mb-2">
                            {aktivni.map((member) => {
                                const vybrany = values.participantIds.includes(member.id)
                                return (
                                    <div key={member.id} className="d-flex align-items-center gap-2 px-2 py-1 border-bottom">
                                        <Form.Check
                                            type="checkbox"
                                            id={`ucastnik-${member.id}`}
                                            checked={vybrany}
                                            label={member.displayName}
                                            className="flex-grow-1"
                                            disabled={!canWrite}
                                            onChange={(event) => prepniUcastnika(member.id, event.target.checked)}
                                        />
                                        {vybrany && amountTotal > 0 && (
                                            <span className="small text-secondary">
                                                {formatAmount(Math.floor(amountTotal / values.participantIds.length), exponent)}
                                            </span>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {form.fieldErrors.split && <div className="invalid-feedback d-block mb-2">{form.fieldErrors.split}</div>}

                    <TextField
                        label="Poznámka"
                        value={values.note}
                        onChange={(hodnota) => setValue('note', hodnota)}
                        error={form.fieldErrors.note}
                        disabled={!canWrite}
                    />

                    <div className="d-grid">
                        <SubmitButton submitting={form.submitting} disabled={!canWrite}>
                            {transaction ? 'Uložit změny' : 'Potvrdit a založit útratu'}
                        </SubmitButton>
                    </div>
                </Card.Body>
            </Card>
        </form>
    )
}
